import React from 'react';
import { FormattedHTMLMessage, injectIntl, InjectedIntl } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationStepsEnum,
    isFormFilled,
    hasFailedInstantMatch,
    setRef,
    VerificationService,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    FormSelectChoice,
    Locale,
    MemberPersonalInfoViewModel,
    MemberPersonalInfoResponse,
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    getAvailableLocaleChoices,
    PhoneNumberComponent as PhoneNumber,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    FormFooterComponent as FormFooter,
    ChangeLocaleComponent,
    MarketConsentWrapper,
    RewardsRemainingComponent,
    FieldId,
    CountryComponentWrapper,
    getSafe,
} from '@sheerid/jslib';

import {
    TEXT_AREA_ROW_ONE,
    TEXT_AREA_ROW_TWO,
    TEXT_AREA_ROW_FOUR,
    TEXT_AREA_COL_FIFTY,
} from '../../lib/constants';

import { RuleConsentWrapperComponent } from '../FormFields/RuleConsentWrapper/RuleConsentWrapperComponent';
import { ProjectNameComponent } from '../FormFields/ProjectName/ProjectNameComponent';
import { ProjectDescriptionComponent } from '../FormFields/ProjectDescription/ProjectDescriptionComponent';
import { PublicProjectComponent } from '../FormFields/PublicProject/PublicProjectComponent';
import { OSILicenseComponent } from '../FormFields/OSILicense/OSILicenseComponent';
import { VerifyProjectComponent } from '../FormFields/VerifyProject/VerifyProjectComponent';


interface StepMemberPersonalInfoComponentProps {
    intl: InjectedIntl;
    verificationService: VerificationService;
}

const StepMemberPersonalInfo = ({ intl, verificationService }: StepMemberPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as MemberPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as MemberPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocaleChoices(verificationService.programTheme, intl);
    const defaultLocaleChoice: FormSelectChoice<Locale, string> = { value: 'en-US', label: 'English' };

    const updateMemberViewModel = (key: keyof MemberPersonalInfoViewModel, value: any) => {
        const nextState: MemberPersonalInfoViewModel = produce(viewModel, (draft: MemberPersonalInfoViewModel) => {
            (draft[key] as any) = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
        const nextState: MemberPersonalInfoViewModel = produce(viewModel, (draft: MemberPersonalInfoViewModel) => {
            draft.localeChoice = localeChoice;
        });
        verificationService.updateLocale(
            nextState,
            verificationService.programTheme,
            verificationService.verificationResponse.segment,
        );
    };

    const getProductSupportChoices = (): FormSelectChoice[] => {
        const availableProductSupportChoices: String[] = ["Yes", "No"];

        // @ts-ignore
        return availableProductSupportChoices
            .map(FormFieldValue => ({
                value: intl.formatMessage({
                    id: `step.personalInfo.productSupportChoices.${FormFieldValue}`,
                    defaultMessage: FormFieldValue as string,
                }),
                label: FormFieldValue,
            }));
    };

    return (
        <div id="sid-step-member-personal-info" className="sid-l-container">
            { failedInstantMatch ?
                <div className="sid-header">
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Email" />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Your full name and email address must match what is on record with your school." />
                    </div>
                </div>
            :
                <div className="sid-header">
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Members-Only Offer" />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a current member of an eligible organization." />
                        <RewardsRemainingComponent verificationService={verificationService} />
                    </div>
                </div>
            }

            {
                localeChoices.length > 1 ?
                    <ChangeLocaleComponent
                        options={localeChoices}
                        value={viewModel.localeChoice || defaultLocaleChoice}
                        isErrored={false}
                        onChange={(localeChoice: FormSelectChoice<Locale, string>) =>
                            updateLocale(localeChoice)}
                    />
                : null
            }

            <FirstName
                value={viewModel.firstName}
                isErrored={!!fieldValidationErrors.firstName}
                onChange={
                    (newValue: any) => {
                        updateMemberViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }
                }
            />

            <LastName
                value={viewModel.lastName}
                isErrored={!!fieldValidationErrors.lastName}
                onChange={
                    (newValue: any) => {
                        updateMemberViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }
                }
            />

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Needed to send you your unique code"
                    />
                }
                onChange={
                    (newValue: any) => {
                        updateMemberViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: any) => handleEmailOnKeyDown(event)}
            />

            <CountryComponentWrapper
                verificationService={verificationService}
                viewModel={viewModel}
                nextFocusField={"projectName"}
            />

            <ProjectNameComponent
                value={getSafe(() => viewModel.metadata['projectName'], '')}
                isErrored={!!fieldValidationErrors.projectName}
                onChange={
                    (newValue: string) => {
                        updateMemberViewModel('metadata', { ...verificationService.viewModel.metadata, projectName: newValue });
                    }
                }
            />

            <ProjectDescriptionComponent
                value={getSafe(() => viewModel.metadata['projectDescription'], '') as string}
                onChange={
                    (newValue: string) => {
                        updateMemberViewModel('metadata', { ...verificationService.viewModel.metadata, projectDescription: newValue });
                    }
                }
                placeholder={
                    intl.formatMessage({
                        id: 'step.personalInfo.projectDescriptionPlaceholder',
                        defaultMessage: 'Describe your project in 1-3 sentences.',
                    })
                }
                isErrored={!!fieldValidationErrors.projectDescription}
                rows={TEXT_AREA_ROW_ONE}
                cols={TEXT_AREA_COL_FIFTY}
            />

            <PublicProjectComponent
                value={getSafe(() => viewModel.metadata['publicProject'], '') as string}
                placeholder={
                    intl.formatMessage({
                        id: 'step.personalInfo.publicProjectPlaceholder',
                        defaultMessage: 'Add a link to your publicly visible project on GitLab. Example: gitlab.com/your-project-namespace',
                    })
                }
                isErrored={!!fieldValidationErrors.publicProject}
                rows={TEXT_AREA_ROW_TWO}
                cols={TEXT_AREA_COL_FIFTY}
                onChange={
                    (newValue: string) => {
                        updateMemberViewModel('metadata', { ...verificationService.viewModel.metadata, publicProject: newValue });
                    }
                }
            />

            <OSILicenseComponent
                value={getSafe(() => viewModel.metadata['OSIlicense'], '') as string}
                placeholder={
                    intl.formatMessage({
                        id: 'step.personalInfo.OSIlicensePlaceholder',
                        defaultMessage: 'Link to a license file in your GitLab instance',
                    })
                }
                isErrored={!!fieldValidationErrors.OSIlicense}
                rows={TEXT_AREA_ROW_ONE}
                cols={TEXT_AREA_COL_FIFTY}
                onChange={
                    (newValue: string) => {
                        updateMemberViewModel('metadata', { ...verificationService.viewModel.metadata, OSIlicense: newValue });
                    }
                }
            />

            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled ?
                    <PhoneNumber
                        isRequired={!!verificationService.programTheme.smsLoopEnabled}
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={
                            (newValue: any) => {
                                updateMemberViewModel('metadata', { ...verificationService.viewModel.metadata, phoneNumber: newValue });
                                updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                            }
                        }
                    />
                : null
            }

            <RuleConsentWrapperComponent
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.ruleOptIn}
                onChange={(newValue: boolean) => {
                    updateMemberViewModel('metadata', { ...verificationService.viewModel.metadata, ruleOptIn: newValue });
                    updateFieldValidationErrorsByFieldId('ruleOptIn', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <VerifyProjectComponent
                value={getSafe(() => viewModel.metadata['verifyProject'], '') as string}
                placeholder={
                    intl.formatMessage({
                        id: 'step.personalInfo.verifyProjectPlaceholder',
                        defaultMessage: '(Optional) Please add any links that will help us verify that this project does not generate money for you. Fundraising campaigns to support your project are ok.',
                    })
                }
                isErrored={!!fieldValidationErrors.verifyProject}
                rows={TEXT_AREA_ROW_FOUR}
                cols={TEXT_AREA_COL_FIFTY}
                onChange={
                    (newValue: string) => {
                        updateMemberViewModel('metadata', { ...verificationService.viewModel.metadata, verifyProject: newValue });
                    }
                }
            />

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue: any) => {
                    updateMemberViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectMemberPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-labelledby="verify-status-text"
                        ref={button => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            { failedInstantMatch ?
                                <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                            :
                                <FormattedHTMLMessage id="verifyMyMembershipStatus" defaultMessage="Verify My Membership Status" />
                            }
                        </span>
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService} />
        </div>
    );
};

export const StepMemberPersonalInfoComponent = injectIntl(StepMemberPersonalInfo);
